'use client';

/**
 * Registers the XOFFI Core service worker (public/sw.js, built from app/sw.js by
 * serwist) and drives a safe update flow:
 *
 *  - Instant paint: the SW serves the last-cached shell (stale-while-revalidate),
 *    then refreshes in the background.
 *  - Background version check: registration.update() on an hourly interval and
 *    whenever the tab regains focus. A new build → new sw.js content → the browser
 *    installs the update and parks it in `waiting`.
 *  - Update prompt: a persistent toast with a "reload" action. We NEVER skip-waiting
 *    silently (avoids swapping the shell mid-session / half-old-half-new state).
 *  - Loop-safe reload: we reload on `controllerchange` ONLY if the user initiated
 *    the update. First-install controllerchange (clientsClaim) does not reload.
 *
 * Only active in production. The core dev box runs `next dev --turbopack` where
 * serwist is disabled and no real sw.js exists — registering there would 404 /
 * serve the kill-switch placeholder.
 */
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

const SW_URL = '/sw.js';
const UPDATE_CHECK_INTERVAL_MS = 60 * 60 * 1000; // hourly

export function ServiceWorkerProvider() {
  const { t } = useTranslation();
  const reloadInitiatedRef = useRef(false);
  const toastShownRef = useRef(false);

  useEffect(() => {
    if (typeof window === 'undefined' || !('serviceWorker' in navigator)) return;

    // react.xoffi.com = next dev. Der Serwist-SW (/sw.js) darf dort nicht
    // registriert werden (HMR/Caching). Ohne JEDEN SW bleibt „Zum Home“ ein
    // Lesezeichen MIT URL-Leiste. Deshalb nur hier der Mini-SW /sw-pwa.js.
    if (process.env.NODE_ENV !== 'production') {
      navigator.serviceWorker
        // updateViaCache:'none' — sonst darf der Browser das SW-Skript selbst
        // bis zu 24h aus dem HTTP-Cache bedienen, und eine Korrektur am Worker
        // käme auf dem Gerät schlicht nicht an.
        .register('/sw-pwa.js', { scope: '/', updateViaCache: 'none' })
        .catch((err) => console.warn('[SW] pwa-install register failed:', err?.message));
      return undefined;
    }

    let registration;
    let intervalId;

    const triggerUpdate = (worker) => {
      if (!worker) return;
      reloadInitiatedRef.current = true;
      worker.postMessage({ type: 'SKIP_WAITING' });
    };

    const promptUpdate = (worker) => {
      if (!worker || toastShownRef.current) return;
      toastShownRef.current = true;
      // Feste ID: sonner zeigt pro ID höchstens EINEN Toast — auch wenn der
      // Effect mehrfach läuft (Sprachwechsel, StrictMode, mehrere Provider).
      toast(t('sw.update.title', { defaultValue: 'Neue Version verfügbar' }), {
        id: 'sw-update-available',
        onDismiss: () => { toastShownRef.current = false; },
        description: t('sw.update.description', {
          defaultValue: 'Lade neu, um die aktuelle Version zu verwenden.',
        }),
        duration: Infinity,
        action: {
          label: t('sw.update.reload', { defaultValue: 'Neu laden' }),
          onClick: () => triggerUpdate(worker),
        },
      });
    };

    const watchInstalling = (reg) => {
      const installing = reg.installing;
      if (!installing) return;
      installing.addEventListener('statechange', () => {
        // controller present => an existing SW controls the page => this is an
        // UPDATE (not the first install), so offer a reload.
        if (installing.state === 'installed' && navigator.serviceWorker.controller) {
          promptUpdate(reg.waiting || installing);
          toastShownRef.current = false; // allow re-prompt if dismissed and re-updated
        }
      });
    };

    navigator.serviceWorker
      .register(SW_URL, { scope: '/', updateViaCache: 'none' })
      .then((reg) => {
        registration = reg;
        // A worker updated in a previous session/tab may already be waiting.
        if (reg.waiting && navigator.serviceWorker.controller) {
          promptUpdate(reg.waiting);
        }
        reg.addEventListener('updatefound', () => watchInstalling(reg));
        intervalId = setInterval(() => reg.update().catch(() => {}), UPDATE_CHECK_INTERVAL_MS);
      })
      .catch((err) => console.warn('[SW] register failed:', err?.message));

    const onVisible = () => {
      if (document.visibilityState === 'visible' && registration) {
        registration.update().catch(() => {});
      }
    };
    document.addEventListener('visibilitychange', onVisible);

    // The new SW has taken control. Reload once — but only if WE asked for it, so
    // first-install (clientsClaim) controllerchange never triggers a spurious reload.
    const onControllerChange = () => {
      if (!reloadInitiatedRef.current) return;
      reloadInitiatedRef.current = false;
      window.location.reload();
    };
    navigator.serviceWorker.addEventListener('controllerchange', onControllerChange);

    return () => {
      if (intervalId) clearInterval(intervalId);
      document.removeEventListener('visibilitychange', onVisible);
      navigator.serviceWorker.removeEventListener('controllerchange', onControllerChange);
    };
  }, [t]);

  return null;
}
